'use client';

/* eslint-disable no-nested-ternary */
import type { ComponentProps, DOMAttributes, ReactElement, ReactNode, Ref } from 'react';
import React, { createElement, forwardRef, useMemo } from 'react';

import classNames from 'classnames';

import type { CssMeasure } from '@utilities/helpers/css';
import { Link } from '@utilities/Link';

import styles from './Button.module.scss';

type ButtonVariant = 'primary' | 'secondary' | 'tertiary' | 'overlay';

type StandardButtonSubVariant = 'ghost';

type ButtonSize = 'small' | 'medium' | 'large' | 'xlarge';

type ButtonCaptionOverflowBehavior = 'clip' | 'ellipsis';

type ButtonIcon = false | ReactElement<any, 'svg'>;

type ButtonLabel = false | string | ReactElement<DOMAttributes<HTMLSpanElement>, 'span'>;

type ButtonElements =
  //  A rectangular button with a caption
  | ButtonLabel
  //  A square icon-only button
  | ButtonIcon
  //  A rectangular button with a caption and an icon on its left
  | [ButtonIcon, ButtonLabel]
  //  A rectangular button with a caption and an icon on its right
  | [ButtonLabel, ButtonIcon]
  //  A rectangular button with a caption and icons on both sides
  | [ButtonIcon, ButtonLabel, ButtonIcon]
  //  A button with no content
  | undefined
  | null
  | false;

const normalizeElements = (elements: ButtonElements) => {
  if (typeof elements === 'string') {
    return [elements];
  }

  if (!elements) {
    return [];
  }

  if (!Array.isArray(elements)) {
    return [false, elements, false];
  }

  if (elements.length === 2) {
    if (typeof elements[0] === 'string') {
      return [false, ...elements];
    } else {
      return [...elements, false];
    }
  }

  return elements;
};

const wrapElements = (elements: Array<ReactNode>) => {
  return elements.map((element, index) => {
    if (typeof element === 'string') {
      return <span key={index}>{element}</span>;
    }

    return element;
  });
};

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface ButtonAccessibility {}

interface BaseButtonProps {
  size?: ButtonSize;
  disabled?: boolean;
  minWidth?: boolean | CssMeasure;
  maxWidth?: CssMeasure;
  width?: CssMeasure;
  overflow?: ButtonCaptionOverflowBehavior;
  label?: string;
  accessibility?: ButtonAccessibility;
  children?: ButtonElements;
}

interface CustomButtonProps {
  variant: 'custom';
  className?: string;
  subVariant?: never;
}

type AccentButtonSubVariant = 'neutral' | 'pink' | 'green' | 'red' | 'orange';

interface AccentButtonProps {
  variant: 'accent';
  subVariant: AccentButtonSubVariant;
  state: 'on' | 'off';
  className?: never;
}

interface StandardButtonProps {
  variant?: ButtonVariant;
  subVariant?: StandardButtonSubVariant;
  className?: never;
}

type OrdinaryButtonProps = BaseButtonProps & Pick<ComponentProps<'button'>, 'onClick' | 'type'>;

type LinkButtonProps = BaseButtonProps & Pick<ComponentProps<'a'>, 'href' | 'target'>;

type VariantProps = StandardButtonProps | AccentButtonProps | CustomButtonProps;

type ButtonProps = (VariantProps & OrdinaryButtonProps) | (VariantProps & LinkButtonProps);

const DEFAULT_MIN_WIDTH = '116px';

type ButtonElement = HTMLButtonElement & HTMLAnchorElement;

type ButtonRef = Ref<HTMLButtonElement> & Ref<HTMLAnchorElement>;

const Button = forwardRef(
  (
    {
      variant = 'primary',
      subVariant,
      size = 'medium',
      disabled,
      overflow = 'clip',
      children,
      minWidth,
      maxWidth,
      width,
      className,
      ...rest
    }: ButtonProps,
    ref: ButtonRef,
  ) => {
    const elements = wrapElements(normalizeElements(children));

    const label = useMemo(() => {
      return (
        rest.label ??
        (React.Children.toArray(children)
          .filter((node) => typeof node === 'string')
          .join(' ') ||
          undefined)
      );
    }, [children, rest.label]);

    const commonProps = {
      'data-variant': variant,
      'data-sub-variant': subVariant,
      'data-state': 'state' in rest ? rest.state : 'off',
      'data-size': size,
      'data-overflow-behavior': overflow,
      'aria-label': label,
      disabled: disabled,
      className: classNames(styles.button, className),
      title: label,
      style: {
        minWidth: minWidth
          ? typeof minWidth === 'boolean'
            ? DEFAULT_MIN_WIDTH
            : minWidth
          : undefined,
        maxWidth,
        width,
      },
    } as const;

    return 'href' in rest && rest.href
      ? createElement(
          Link,
          //  Explicitely assigning href is necessary in this case
          //  to suppress TS error indicating that href might be undefined in rest
          { ...commonProps, ...rest, href: rest.href, ref, rel: 'noopener noreferrer' },
          ...elements,
        )
      : createElement<DOMAttributes<HTMLButtonElement>, HTMLElement>(
          'button',
          { ...commonProps, ...rest, ref },
          ...elements,
        );
  },
);

export {
  Button,
  type ButtonSize,
  type ButtonVariant,
  type StandardButtonSubVariant as ButtonSubVariant,
  type ButtonCaptionOverflowBehavior,
  type ButtonElements,
  type BaseButtonProps,
  type StandardButtonProps,
  type OrdinaryButtonProps,
  type AccentButtonProps,
  type CustomButtonProps,
  type LinkButtonProps,
  type ButtonProps,
  type VariantProps,
  type AccentButtonSubVariant,
  type ButtonRef,
  type ButtonElement,
  type ButtonAccessibility,
};
